import { useCallback, useEffect, useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts';
import { formatRelativeTime } from '@shared/formatters';
import type { DashboardSummary, MetricDataPoint, Insight } from '@shared/types';
import {
  getDashboardSummary,
  getMetricsDashboard,
  getSyncStatus,
  getDashboardInsights,
} from '../lib/api-client';
import type { ApiResponse, SyncStatus } from '../lib/api-client';
import { ResourceModal } from '../components/ResourceModal';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';
import {
  Loader2,
  AlertTriangle,
  Server,
  ShieldAlert,
  ShieldX,
  ShieldCheck,
  RefreshCw,
  Clock,
  Lightbulb,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  Activity,
  X,
  Calendar,
} from 'lucide-react';

type MetricRange = '15m' | '30m' | '1h' | '6h' | '12h' | '24h' | '7d' | '30d' | 'custom';

export function DashboardView() {
  const [summary, setSummary] = useState<ApiResponse<DashboardSummary> | null>(null);
  const [metrics, setMetrics] = useState<ApiResponse<MetricDataPoint[]> | null>(null);
  const [syncStatus, setSyncStatus] = useState<ApiResponse<SyncStatus> | null>(null);
  const [insights, setInsights] = useState<ApiResponse<Insight[]> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metricRange, setMetricRange] = useState<MetricRange>('1h');
  const [customStart, setCustomStart] = useState<string>('');
  const [customEnd, setCustomEnd] = useState<string>('');
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const metricsPromise = metricRange === 'custom' && customStart && customEnd
        ? getMetricsDashboard('1h', customStart, customEnd)
        : getMetricsDashboard(metricRange === 'custom' ? '1h' : metricRange);
      const insightsRange = metricRange === 'custom' ? '1h' : metricRange;
      const [summaryRes, metricsRes, syncRes, insightsRes] = await Promise.all([
        getDashboardSummary(),
        metricsPromise.catch(() => null),
        getSyncStatus().catch(() => null),
        getDashboardInsights(insightsRange).catch(() => null),
      ]);
      setSummary(summaryRes);
      setMetrics(metricsRes);
      setSyncStatus(syncRes);
      setInsights(insightsRes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [metricRange, customStart, customEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="h-12 w-12 rounded-full border-4 border-gray-100" />
            <Loader2 className="absolute inset-0 h-12 w-12 animate-spin text-primary-500" />
          </div>
          <p className="text-sm font-medium text-gray-400">Cargando dashboard…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200/50 bg-gradient-to-r from-red-50 to-white">
        <CardContent className="flex items-center gap-4 p-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-red-900">Error al cargar datos</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isStale = summary?.stale || metrics?.stale || syncStatus?.stale;
  const dashboardData = summary?.data;
  const metricsRaw = metrics?.data;
  const metricsData: MetricDataPoint[] = Array.isArray(metricsRaw) ? metricsRaw : [];
  const chartData = buildChartData(metricsData);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-md shadow-primary-500/20">
              <Activity className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-gray-900">Dashboard</h1>
              <p className="text-sm text-gray-500">
                Monitoreo en tiempo real de recursos, seguridad y red.
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isStale && <StaleIndicator cachedAt={summary?.cachedAt ?? metrics?.cachedAt ?? null} />}
          <Button variant="outline" size="sm" onClick={fetchData} className="gap-2">
            <RefreshCw className="h-3.5 w-3.5" />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {dashboardData && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <SummaryCard title="Total Recursos" value={dashboardData.totalResources} icon={Server} variant="default" />
          <SummaryCard title="Critical" value={dashboardData.findingsBySeverity.critical ?? 0} icon={ShieldX} variant="critical" />
          <SummaryCard title="High" value={dashboardData.findingsBySeverity.high ?? 0} icon={ShieldAlert} variant="high" />
          <SummaryCard title="Medium" value={dashboardData.findingsBySeverity.medium ?? 0} icon={AlertTriangle} variant="medium" />
          <SummaryCard title="Low" value={dashboardData.findingsBySeverity.low ?? 0} icon={ShieldCheck} variant="low" />
          <LastSyncCard lastSyncAt={dashboardData.lastSyncAt} lastSyncStatus={dashboardData.lastSyncStatus} />
        </div>
      )}

      {/* Metrics Charts */}
      <Card className="overflow-hidden">
        <CardHeader className="flex-row items-center justify-between space-y-0 border-b bg-gray-50/50 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100">
              <Activity className="h-4 w-4 text-indigo-600" />
            </div>
            <CardTitle className="text-base font-semibold">Network Metrics</CardTitle>
          </div>
          <MetricRangeSelector selected={metricRange} onChange={setMetricRange} onCustomClick={() => setShowCustomModal(true)} />
        </CardHeader>
        <CardContent className="p-6">
          {metricsData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Activity className="mb-3 h-10 w-10 text-gray-200" />
              <p className="text-sm font-medium text-gray-400">Sin métricas disponibles</p>
              <p className="mt-1 text-xs text-gray-300">Selecciona otro rango de tiempo</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-indigo-500" />
                  <h3 className="text-sm font-medium text-gray-700">Throughput</h3>
                  <span className="text-xs text-gray-400">(bytes/s)</span>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={chartData.throughput}>
                    <defs>
                      <linearGradient id="throughputGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6366f1" stopOpacity={0.15} />
                        <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={formatChartTime} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <RechartsTooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} labelFormatter={formatTooltipTime} />
                    <Area type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2} fill="url(#throughputGradient)" name="Throughput" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-rose-500" />
                  <h3 className="text-sm font-medium text-gray-700">Latency p99</h3>
                  <span className="text-xs text-gray-400">(ms)</span>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={chartData.latency}>
                    <defs>
                      <linearGradient id="latencyGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f43f5e" stopOpacity={0.15} />
                        <stop offset="100%" stopColor="#f43f5e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={formatChartTime} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <RechartsTooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} labelFormatter={formatTooltipTime} />
                    <ReferenceLine y={2000} stroke="#6366f1" strokeDasharray="4 4" strokeOpacity={0.5} />
                    <ReferenceLine y={5000} stroke="#f59e0b" strokeDasharray="4 4" strokeOpacity={0.5} />
                    <Area type="monotone" dataKey="value" stroke="#f43f5e" strokeWidth={2} fill="url(#latencyGradient)" name="Latency" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Resources */}
      {metricsData.length > 0 && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <TopResourcesTable title="Top LBs por Throughput" data={getTopResources(metricsData, 'throughput')} unit="bytes/s" onResourceClick={setSelectedResourceId} icon={TrendingUp} iconColor="text-indigo-500" />
          <TopResourcesTable title="Top LBs por Latency (p99)" data={getTopResources(metricsData, 'latency')} unit="ms" onResourceClick={setSelectedResourceId} icon={TrendingDown} iconColor="text-rose-500" />
        </div>
      )}

      {/* Insights */}
      {insights?.data && insights.data.length > 0 && (
        <InsightsPanel insights={insights.data} onResourceClick={setSelectedResourceId} />
      )}

      {/* Custom Range Modal */}
      {showCustomModal && (
        <CustomRangeModal
          onApply={(start, end) => { setCustomStart(start); setCustomEnd(end); setMetricRange('custom'); setShowCustomModal(false); }}
          onClose={() => setShowCustomModal(false)}
        />
      )}

      {/* Resource Detail Modal */}
      {selectedResourceId && (
        <ResourceModal resourceId={selectedResourceId} onClose={() => setSelectedResourceId(null)} />
      )}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function SummaryCard({ title, value, icon: Icon, variant }: {
  title: string;
  value: number;
  icon: React.ElementType;
  variant: 'default' | 'critical' | 'high' | 'medium' | 'low';
}) {
  const styles = {
    default: { bg: 'bg-gradient-to-br from-white to-primary-50/30', icon: 'bg-primary-100 text-primary-600', border: 'border-primary-100', text: 'text-gray-900' },
    critical: { bg: 'bg-gradient-to-br from-white to-red-50/30', icon: 'bg-red-100 text-red-600', border: 'border-red-100', text: 'text-red-700' },
    high: { bg: 'bg-gradient-to-br from-white to-orange-50/30', icon: 'bg-orange-100 text-orange-600', border: 'border-orange-100', text: 'text-orange-700' },
    medium: { bg: 'bg-gradient-to-br from-white to-yellow-50/30', icon: 'bg-yellow-100 text-yellow-600', border: 'border-yellow-100', text: 'text-yellow-700' },
    low: { bg: 'bg-gradient-to-br from-white to-green-50/30', icon: 'bg-green-100 text-green-600', border: 'border-green-100', text: 'text-green-700' },
  };
  const s = styles[variant];

  return (
    <Card className={cn('group transition-all duration-200 hover:shadow-md hover:-translate-y-0.5', s.bg, s.border)}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{title}</p>
          <div className={cn('rounded-lg p-2 transition-transform group-hover:scale-110', s.icon)}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <p className={cn('mt-3 text-3xl font-bold tabular-nums', s.text)}>
          {value.toLocaleString()}
        </p>
      </CardContent>
    </Card>
  );
}

function LastSyncCard({ lastSyncAt, lastSyncStatus }: {
  lastSyncAt: string | null;
  lastSyncStatus: 'running' | 'completed' | 'failed';
}) {
  const relativeTime = lastSyncAt ? formatRelativeTime(lastSyncAt) : 'Never';
  const absoluteTime = lastSyncAt ? new Date(lastSyncAt).toLocaleString() : 'No sync recorded';
  const statusConfig = {
    running: { badge: 'info' as const, icon: RefreshCw, label: 'Syncing', animate: true },
    completed: { badge: 'success' as const, icon: Clock, label: 'OK', animate: false },
    failed: { badge: 'critical' as const, icon: AlertTriangle, label: 'Error', animate: false },
  };
  const config = statusConfig[lastSyncStatus];
  const StatusIcon = config.icon;

  return (
    <Card className="group border-gray-100 bg-gradient-to-br from-white to-gray-50/50 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5" title={absoluteTime}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Última Sync</p>
          <Badge variant={config.badge} className="gap-1 text-[10px]">
            <StatusIcon className={cn('h-3 w-3', config.animate && 'animate-spin')} />
            {config.label}
          </Badge>
        </div>
        <p className="mt-3 text-lg font-bold text-gray-900">{relativeTime}</p>
      </CardContent>
    </Card>
  );
}

function StaleIndicator({ cachedAt }: { cachedAt: string | null }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5">
      <div className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
      <span className="text-xs font-medium text-amber-700">Datos en caché</span>
      {cachedAt && <span className="text-[10px] text-amber-500">({new Date(cachedAt).toLocaleTimeString()})</span>}
    </div>
  );
}

function MetricRangeSelector({ selected, onChange, onCustomClick }: {
  selected: MetricRange;
  onChange: (range: MetricRange) => void;
  onCustomClick: () => void;
}) {
  const ranges: { label: string; value: MetricRange }[] = [
    { label: '15m', value: '15m' },
    { label: '30m', value: '30m' },
    { label: '1h', value: '1h' },
    { label: '6h', value: '6h' },
    { label: '12h', value: '12h' },
    { label: '24h', value: '24h' },
    { label: '7d', value: '7d' },
    { label: '30d', value: '30d' },
  ];

  return (
    <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
      {ranges.map((r) => (
        <button
          key={r.value}
          onClick={() => onChange(r.value)}
          className={cn(
            'rounded-md px-2.5 py-1.5 text-xs font-medium transition-all duration-150',
            selected === r.value
              ? 'bg-gray-900 text-white shadow-sm'
              : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
          )}
        >
          {r.label}
        </button>
      ))}
      <button
        onClick={onCustomClick}
        className={cn(
          'flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium transition-all duration-150',
          selected === 'custom'
            ? 'bg-gray-900 text-white shadow-sm'
            : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
        )}
      >
        <Calendar className="h-3 w-3" />
        Custom
      </button>
    </div>
  );
}

function CustomRangeModal({ onApply, onClose }: {
  onApply: (start: string, end: string) => void;
  onClose: () => void;
}) {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md animate-in fade-in-0 zoom-in-95 rounded-2xl border border-gray-200 bg-white p-0 shadow-2xl">
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100">
              <Calendar className="h-4 w-4 text-primary-600" />
            </div>
            <h3 className="text-base font-semibold text-gray-900">Rango personalizado</h3>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600">
            <X className="h-4 w-4" />
          </button>
        </div>
        {/* Modal Body */}
        <div className="space-y-4 px-6 py-5">
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-gray-500">Desde</label>
            <input
              type="datetime-local"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm transition-all focus:border-primary-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-gray-500">Hasta</label>
            <input
              type="datetime-local"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm transition-all focus:border-primary-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </div>
        </div>
        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 px-6 py-4">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!start || !end}
            onClick={() => { if (start && end) onApply(new Date(start).toISOString(), new Date(end).toISOString()); }}
          >
            Aplicar rango
          </Button>
        </div>
      </div>
    </div>
  );
}

function TopResourcesTable({ title, data, unit, onResourceClick, icon: Icon, iconColor }: {
  title: string;
  data: Array<{ resourceId: string; name: string; avgValue: number }>;
  unit: string;
  onResourceClick: (id: string) => void;
  icon: React.ElementType;
  iconColor: string;
}) {
  const isLatency = unit === 'ms';

  function getValueStyle(value: number) {
    if (!isLatency) return { color: 'text-gray-900', bg: '' };
    if (value >= 5000) return { color: 'text-red-600', bg: 'bg-red-50' };
    if (value >= 2000) return { color: 'text-amber-600', bg: 'bg-amber-50' };
    return { color: 'text-emerald-600', bg: 'bg-emerald-50' };
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex-row items-center gap-3 space-y-0 border-b bg-gray-50/50 px-5 py-3.5">
        <Icon className={cn('h-4 w-4', iconColor)} />
        <CardTitle className="text-sm font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {data.length === 0 ? (
          <p className="p-5 text-center text-sm text-gray-400">Sin datos</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {data.map((item, i) => {
              const style = getValueStyle(item.avgValue);
              return (
                <div
                  key={item.resourceId}
                  onClick={() => onResourceClick(item.resourceId)}
                  className="flex items-center gap-3 px-5 py-3 transition-colors cursor-pointer hover:bg-gray-50/80 group"
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[11px] font-bold text-gray-400 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors">
                    {i + 1}
                  </span>
                  <span className="flex-1 truncate text-sm text-gray-700 group-hover:text-gray-900 transition-colors">{item.name}</span>
                  <span className={cn('shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold tabular-nums', style.color, style.bg)}>
                    {item.avgValue.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                    <span className="ml-0.5 font-normal text-gray-400">{unit}</span>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function InsightsPanel({ insights, onResourceClick }: {
  insights: Insight[];
  onResourceClick: (id: string) => void;
}) {
  const [showInfoInsights, setShowInfoInsights] = useState(false);
  const infraInsights = insights.filter(i => i.severity !== 'info');
  const appInsights = insights.filter(i => i.severity === 'info');
  const visibleInsights = showInfoInsights ? insights : infraInsights;

  const severityConfig: Record<string, { icon: string; badge: 'critical' | 'warning' | 'info'; accent: string }> = {
    critical: { icon: '🔴', badge: 'critical', accent: 'border-l-red-500' },
    warning: { icon: '🟡', badge: 'warning', accent: 'border-l-amber-400' },
    info: { icon: 'ℹ️', badge: 'info', accent: 'border-l-blue-400' },
  };

  const categoryLabels: Record<string, string> = {
    latency: 'Latencia',
    throughput: 'Throughput',
    security_correlation: 'Seguridad + Infra',
    availability: 'Disponibilidad',
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex-row items-center justify-between space-y-0 border-b bg-gray-50/50 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100">
            <Lightbulb className="h-4 w-4 text-amber-600" />
          </div>
          <CardTitle className="text-base font-semibold">Insights & Recomendaciones</CardTitle>
          {infraInsights.length > 0 && <Badge variant="critical">{infraInsights.length} infra</Badge>}
          {appInsights.length > 0 && <Badge variant="secondary">{appInsights.length} app</Badge>}
        </div>
        {appInsights.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => setShowInfoInsights(!showInfoInsights)} className="text-xs">
            {showInfoInsights ? 'Ocultar app' : `Ver ${appInsights.length} app-level`}
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {visibleInsights.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <ShieldCheck className="mb-2 h-8 w-8 text-emerald-200" />
            <p className="text-sm font-medium text-gray-400">Sin problemas de infraestructura detectados</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {visibleInsights.map((insight) => {
              const config = severityConfig[insight.severity] || severityConfig.info;
              return (
                <div key={insight.id} className={cn('border-l-4 p-5 transition-colors hover:bg-gray-50/50', config.accent)}>
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 text-base">{config.icon}</span>
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold text-gray-900">{insight.title}</h3>
                        <Badge variant={config.badge} className="text-[10px]">{insight.severity}</Badge>
                        <Badge variant="secondary" className="text-[10px]">{categoryLabels[insight.category] || insight.category}</Badge>
                      </div>
                      <p className="text-sm leading-relaxed text-gray-600">{insight.description}</p>
                      <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-2.5">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-gray-800">Recomendación:</span> {insight.recommendation}
                        </p>
                      </div>
                      <div className="flex items-center gap-4 flex-wrap pt-1">
                        <button
                          onClick={() => onResourceClick(insight.resourceId)}
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-800 transition-colors"
                        >
                          <ExternalLink className="h-3 w-3" />
                          {insight.resourceName}
                        </button>
                        {insight.metricValue > 0 && (
                          <span className="text-xs text-gray-400">
                            Valor: <span className="font-medium text-gray-600">{insight.metricValue.toLocaleString()}</span>
                            {insight.category === 'latency' || insight.category === 'security_correlation' ? 'ms' : ' bytes/s'}
                            {insight.threshold > 0 && ` (umbral: ${insight.threshold.toLocaleString()})`}
                          </span>
                        )}
                        {insight.relatedFindingIds.length > 0 && (
                          <Badge variant="critical" className="text-[10px]">{insight.relatedFindingIds.length} finding(s)</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function getTopResources(
  dataPoints: MetricDataPoint[],
  metricType: 'throughput' | 'latency'
): Array<{ resourceId: string; name: string; avgValue: number }> {
  const map = new Map<string, { values: number[]; name: string }>();
  for (const point of dataPoints) {
    if (point.metricType !== metricType) continue;
    const existing = map.get(point.resourceId) || { values: [] as number[], name: point.resourceName || point.resourceId.slice(0, 8) };
    existing.values.push(point.value);
    if (point.resourceName) existing.name = point.resourceName;
    map.set(point.resourceId, existing);
  }
  return Array.from(map.entries())
    .map(([resourceId, { values, name }]) => ({
      resourceId,
      name,
      avgValue: values.reduce((s, v) => s + v, 0) / values.length,
    }))
    .sort((a, b) => b.avgValue - a.avgValue)
    .slice(0, 5);
}

interface ChartPoint { time: string; value: number; }

function buildChartData(dataPoints: MetricDataPoint[]): { throughput: ChartPoint[]; latency: ChartPoint[] } {
  const throughputMap = new Map<string, number[]>();
  const latencyMap = new Map<string, number[]>();
  for (const point of dataPoints) {
    const date = new Date(point.timestamp);
    date.setMinutes(Math.floor(date.getMinutes() / 5) * 5, 0, 0);
    const roundedTime = date.toISOString();
    const map = point.metricType === 'throughput' ? throughputMap : latencyMap;
    const existing = map.get(roundedTime) || [];
    existing.push(point.value);
    map.set(roundedTime, existing);
  }
  const throughput: ChartPoint[] = Array.from(throughputMap.entries())
    .map(([time, values]) => ({ time, value: Math.round(values.reduce((s, v) => s + v, 0) / values.length) }))
    .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
  const latency: ChartPoint[] = Array.from(latencyMap.entries())
    .map(([time, values]) => ({ time, value: Math.round(values.reduce((s, v) => s + v, 0) / values.length) }))
    .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
  return { throughput, latency };
}

function formatChartTime(timestamp: string): string {
  const d = new Date(timestamp);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatTooltipTime(timestamp: string): string {
  return new Date(timestamp).toLocaleString([], {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}
